import React, {
	PropsWithChildren,
	memo,
	useCallback,
	useMemo,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "uikit";

import useModelSubscribe from "../../../../../../../../../../hooks/useModelSubscribe2";
import SMSProvider from "../../../../../../../../../../services/SMSProvider";
import SMSTemplate from "../../../../../../../../../../services/SMSTemplate";
import Message from "../../../../../../../../../../services/Message";
import PaymentLinkSmsModal, {
	isPaymentLinkTemplate,
} from "../../../../../../../../../../components/PaymentLinkSmsModal";
import { useTypedSelector } from "../../../../../../../../../../redux/store";
import {
	AccessKey,
	generateAccessName,
	hasAccess,
} from "../../../../../../../../../../access";
import { StyledRow } from "../../../../../../../../../../components/common";

import { ButtonFooter } from "../../styled";
import Modal from "./components/Modal";

const BaseSendingSMS: React.FC<BaseSendingSMS.Props> = memo(
	({ phones, mainPhone, taxiServiceId, disabled }) => {
		const { t } = useTranslation();
		const { models: providers } = useModelSubscribe({}, SMSProvider);
		const { models: templates } = useModelSubscribe({}, SMSTemplate);
		const [showModal, setShowModal] = useState(false);

		const notOrdersTemplates = useMemo(
			() => templates.filter((t) => !t.orderTemplate),
			[templates],
		);

		const personalRole = useTypedSelector(
			(state) => state.account.personalRole,
		);
		const hasPaymentLinkAccess = useMemo(
			() =>
				hasAccess(
					generateAccessName(
						AccessKey.ORDERS,
						AccessKey.PAYMENT_LINK_SMS,
					),
					personalRole,
				),
			[personalRole],
		);

		// Payment link templates: listed next to the plain ones, but sending goes
		// through the payment link modal, where the order is picked.
		const paymentLinkTemplates = useMemo(
			() =>
				hasPaymentLinkAccess
					? templates.filter(
							(t) =>
								t.active !== false &&
								isPaymentLinkTemplate(t) &&
								!notOrdersTemplates.includes(t),
					  )
					: [],
			[hasPaymentLinkAccess, notOrdersTemplates, templates],
		);
		const modalTemplates = useMemo(
			() => [...notOrdersTemplates, ...paymentLinkTemplates],
			[notOrdersTemplates, paymentLinkTemplates],
		);
		const [paymentLink, setPaymentLink] = useState<{
			phone?: string;
			smsTemplateId: number;
		} | null>(null);

		const phoneDisabled = useMemo(() => !mainPhone, [mainPhone]);

		const handleShowModal = useCallback(() => {
			setShowModal(true);
		}, []);

		const handleCloseModal = useCallback(() => {
			setShowModal(false);
		}, []);

		const handleSend = useCallback(
			async (value: Message.Send) => {
				const templateId =
					"smsTemplateId" in value.data
						? value.data.smsTemplateId
						: null;
				if (
					templateId &&
					paymentLinkTemplates.some((t) => t.id === templateId)
				) {
					setShowModal(false);
					setPaymentLink({
						phone: value.contacts[0]?.phone ?? mainPhone,
						smsTemplateId: templateId,
					});
					return;
				}
				const res = await Message.send(value);
				if (res) {
					setShowModal(false);
				}
			},
			[mainPhone, paymentLinkTemplates],
		);

		return (
			<StyledRow>
				<ButtonFooter
					disabled={disabled || phoneDisabled}
					variant="secondary"
					// settingsKeyId="copyOrder"
					title={
						t(
							"pages.mainPage.pages.orders.orderModal.footer.sendingSMS.str0",
						) ?? ""
					}
					onClick={handleShowModal}
				>
					<Icon id="orders-sms" size={22} />
				</ButtonFooter>
				{showModal && (
					<Modal
						mainPhone={mainPhone}
						phones={phones}
						providers={providers}
						taxiServiceId={taxiServiceId}
						templates={modalTemplates}
						onClose={handleCloseModal}
						onSubmit={handleSend}
					/>
				)}
				{paymentLink && (
					<PaymentLinkSmsModal
						initialPhone={paymentLink.phone}
						smsTemplateId={paymentLink.smsTemplateId}
						onClose={() => setPaymentLink(null)}
					/>
				)}
			</StyledRow>
		);
	},
);

declare namespace BaseSendingSMS {
	interface Props extends PropsWithChildren {
		taxiServiceId?: number;
		mainPhone?: string;
		phones: string[];
		disabled?: boolean;
	}
}

export const SendingSMS = memo(BaseSendingSMS);
export default BaseSendingSMS;
